import React, { FunctionComponent } from "react";
import { urlBuilderService } from "sprint-common-web";

import { ProtectedRoute, RouteItem } from "../core/routing/ProtectedRoute";
import { SprintAccessRight } from "../core/security/SprintAccessRight";
import { BucketArchiveFilesDashboard } from "./bucket-archive/listing/BucketArchiveFilesDashboard";
import { BucketArchiveListingDashboard } from "./bucket-archive/listing/BucketArchiveListingDashboard";
import { CloudFileDetailsDashboard } from "./cloud-files/details/CloudFileDetailsDashboard";
import { CloudFileListingDashboard } from "./cloud-files/listing/CloudFileListingDashboard";
import { DeliveryBucketExplorer } from "./delivery-buckets/explorer/DeliveryBucketExplorer";
import { DeliveryBucketListingDashboard } from "./delivery-buckets/listing/DeliveryBucketListingDashboard";
import { FileRecordDetailsPage } from "./file-records/details/FileRecordDetailsPage";
import { CreateFileRecordDashboard } from "./file-records/forms/create/CreateFileRecordDashboard";
import { EditFileRecordContentDashboard } from "./file-records/forms/edit/content/EditFileRecordContentDashboard";
import { FileRecordListingDashboard } from "./file-records/listing/FileRecordListingDashboard";
import { DeliveryUploadDashboard } from "./upload/DeliveryUploadDashboard";

export enum AssociateWizardTab {
    TechnicalInfo = 1,
    Languages = 2,
    Associate = 3,
    Summary = 4,
    Reject = 5
}

const contentIngestionBasePath = "/content-ingestion";

export const ciUploadRoute: RouteItem = {
    path: `${contentIngestionBasePath}/upload`,
    component: DeliveryUploadDashboard,
    accessRights: [SprintAccessRight.CiReadWrite],
    name: "Non Aspera Content"
};

export const ciDeliveryBucketListingRoute: RouteItem = {
    path: `${contentIngestionBasePath}/buckets`,
    component: DeliveryBucketListingDashboard,
    accessRights: [SprintAccessRight.CiReadWrite],
    name: "Buckets"
};

export const ciDeliveryBucketRoute: RouteItem = {
    path: `${contentIngestionBasePath}/buckets/:deliveryBucketId/:deliveryBucketName/:bucketUniqueId`,
    component: DeliveryBucketExplorer,
    accessRights: [SprintAccessRight.CiReadWrite],
    name: "Bucket"
};

export const ciBucketsArchiveListingRoute: RouteItem = {
    path: `${contentIngestionBasePath}/buckets-archive`,
    component: BucketArchiveListingDashboard,
    accessRights: [SprintAccessRight.CiReadWrite],
    name: "BucketsArchive"
};

export const ciBucketArchiveRoute: RouteItem = {
    path: `${contentIngestionBasePath}/buckets-archive/:archiveBucketName`,
    component: BucketArchiveFilesDashboard,
    accessRights: [SprintAccessRight.CiReadWrite],
    name: "Bucket Archive Files Dashboard"
};

export const ciCloudFileListingRoute: RouteItem = {
    path: `${contentIngestionBasePath}/cloud-files`,
    component: CloudFileListingDashboard,
    accessRights: [SprintAccessRight.CiReadWrite],
    name: "Ingest"
};

export const ciFileRecordsRoute: RouteItem = {
    path: `${contentIngestionBasePath}/file-records`,
    component: FileRecordListingDashboard,
    accessRights: [SprintAccessRight.CiRead],
    name: "File records"
};

export const ciFileRecordQcReviewRoute: RouteItem = {
    path: `${contentIngestionBasePath}/file-records/get-details/:fileRecordId`,
    component: FileRecordDetailsPage,
    accessRights: [SprintAccessRight.CiRead],
    name: "QC"
};

export const ciCreateFileRecordFormRoute: RouteItem = {
    path: `${contentIngestionBasePath}/file-records/create`,
    component: CreateFileRecordDashboard,
    accessRights: [SprintAccessRight.OrdReadWrite],
    name: "Create file record"
};

export const ciEditFileRecordContentFormRoute: RouteItem = {
    path: `${contentIngestionBasePath}/file-records/edit-content/:fileRecordId`,
    component: EditFileRecordContentDashboard,
    accessRights: [SprintAccessRight.OrdReadWrite],
    name: "Edit file record content"
};

export const ciCloudFileQcReviewRoute: RouteItem = {
    path: `${contentIngestionBasePath}/cloud-files/:cloudFileId`,
    component: CloudFileDetailsDashboard,
    accessRights: [SprintAccessRight.CiReadWrite],
    name: "QC"
};

export const ciRoutes = {
    routeItems: [
        ciUploadRoute,
        ciDeliveryBucketListingRoute,
        ciDeliveryBucketRoute,
        ciBucketsArchiveListingRoute,
        ciCloudFileListingRoute,
        ciCloudFileQcReviewRoute,
        ciFileRecordQcReviewRoute,
        ciFileRecordsRoute,
        ciBucketArchiveRoute,
        ciCreateFileRecordFormRoute,
        ciEditFileRecordContentFormRoute
    ],
    baseRouteNames: ["Content Ingestion"]
};

export const ContentIngestionRoutes: FunctionComponent = () => {
    return (
        <>
            <ProtectedRoute protectedRouteData={ciCloudFileListingRoute} />
            <ProtectedRoute protectedRouteData={ciUploadRoute} />
            <ProtectedRoute protectedRouteData={ciDeliveryBucketListingRoute} />
            <ProtectedRoute protectedRouteData={ciDeliveryBucketRoute} />
            <ProtectedRoute protectedRouteData={ciBucketsArchiveListingRoute} />
            <ProtectedRoute protectedRouteData={ciBucketArchiveRoute} />
            <ProtectedRoute protectedRouteData={ciCloudFileQcReviewRoute} />
            <ProtectedRoute protectedRouteData={ciFileRecordsRoute} />
            <ProtectedRoute protectedRouteData={ciCreateFileRecordFormRoute} />
            <ProtectedRoute protectedRouteData={ciEditFileRecordContentFormRoute} />
            <ProtectedRoute protectedRouteData={ciFileRecordQcReviewRoute} />
        </>
    );
};

const urlBuilder = urlBuilderService();

export const getCiCloudFileQcReviewPageHref = (cloudFileId: string, wizardItem?: AssociateWizardTab): string => {
    const basePath = ciCloudFileQcReviewRoute.path.replace(":cloudFileId", cloudFileId);

    return urlBuilder.buildUrl(basePath, { wizardItem });
};

export const getCiFileRecordQcReviewPageHref = (fileRecordId: string): string => {
    return ciFileRecordQcReviewRoute.path.replace(":fileRecordId", fileRecordId);
};

export const getCiEditFileRecordContentPageHref = (fileRecordId: string): string => {
    return ciEditFileRecordContentFormRoute.path.replace(":fileRecordId", fileRecordId);
};

export const getDeliveryBucketListingHref = (): string => {
    return ciDeliveryBucketListingRoute.path;
};

export const getBucketsArchiveListingHref = (): string => {
    return ciBucketsArchiveListingRoute.path;
};

export const getDeliveryBucketHref = (
    deliveryBucketId: string,
    deliveryBucketName: string,
    deliveryUniqueId: string
): string => {
    return ciDeliveryBucketRoute.path
        .replace(":deliveryBucketId", deliveryBucketId)
        .replace(":deliveryBucketName", deliveryBucketName)
        .replace(":bucketUniqueId", deliveryUniqueId);
};

export const getBucketArchiveHref = (archiveBucketName: string): string => {
    return ciBucketArchiveRoute.path.replace(":archiveBucketName", archiveBucketName);
};

export const getFileRecordDetailsHref = (fileRecordId: string): string => {
    return ciFileRecordQcReviewRoute.path.replace(":fileRecordId", fileRecordId);
};
